import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import {
  fetchAttachmentObjectUrl,
  useChatPolling,
  type ChatAttachment,
  type ChatMessage,
} from '../api/chat'
import { listChatPartners, type ChatPartner } from '../api/partners'
import { listPartnerThreads, markThreadRead, type ChatThreadSummary } from '../api/threads'
import type { UserRole } from '../api/types'
import { devCounterpartyOrgIdForRole, isDevMode } from '../config/devMode'

export type ChatPartnerSelection = {
  organizationId: number
  name: string
}

type ChatScreenProps = {
  organizationId: number
  role: UserRole
  active: boolean
  partner: ChatPartnerSelection | null
  onSelectPartner: (partner: ChatPartnerSelection | null) => void
}

type MessageGroup = {
  dateKey: string
  dateLabel: string
  messages: ChatMessage[]
}

const counterpartyLabel = (role: UserRole): string =>
  role === 'supplier' ? 'покупателем' : 'поставщиком'

const initialsFromName = (name: string): string => {
  const parts = name.trim().split(/\s+/).filter(Boolean)
  if (parts.length === 0) {
    return '?'
  }
  if (parts.length === 1) {
    return parts[0].slice(0, 2).toUpperCase()
  }
  return `${parts[0][0] ?? ''}${parts[1][0] ?? ''}`.toUpperCase()
}

const formatMessageTime = (iso: string): string => {
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) {
    return ''
  }
  return date.toLocaleString('ru-RU', {
    hour: '2-digit',
    minute: '2-digit',
  })
}

const formatThreadTime = (iso: string | null): string => {
  if (!iso) {
    return ''
  }
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) {
    return ''
  }
  const now = new Date()
  const sameDay =
    date.getFullYear() === now.getFullYear()
    && date.getMonth() === now.getMonth()
    && date.getDate() === now.getDate()
  if (sameDay) {
    return date.toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' })
  }
  return date.toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit' })
}

const formatDateLabel = (iso: string): string => {
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) {
    return ''
  }
  const today = new Date()
  const yesterday = new Date()
  yesterday.setDate(today.getDate() - 1)

  const sameDay = (a: Date, b: Date) =>
    a.getFullYear() === b.getFullYear()
    && a.getMonth() === b.getMonth()
    && a.getDate() === b.getDate()

  if (sameDay(date, today)) {
    return 'Сегодня'
  }
  if (sameDay(date, yesterday)) {
    return 'Вчера'
  }
  return date.toLocaleDateString('ru-RU', {
    day: 'numeric',
    month: 'long',
  })
}

const dateKeyOf = (iso: string): string => {
  const date = new Date(iso)
  return Number.isNaN(date.getTime()) ? iso : date.toISOString().slice(0, 10)
}

const groupMessagesByDate = (messages: ChatMessage[]): MessageGroup[] => {
  const groups: MessageGroup[] = []
  for (const message of messages) {
    const dateKey = dateKeyOf(message.createdAt)
    const last = groups[groups.length - 1]
    if (last?.dateKey === dateKey) {
      last.messages.push(message)
    } else {
      groups.push({ dateKey, dateLabel: formatDateLabel(message.createdAt), messages: [message] })
    }
  }
  return groups
}

const SendIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M3.4 20.6 21 12 3.4 3.4l2.8 7.2L16 12l-9.8 1.4-2.8 7.2z" />
  </svg>
)

const AttachIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" aria-hidden="true">
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M21.44 11.05l-9.19 9.19a6 6 0 01-8.49-8.49l9.19-9.19a4 4 0 015.66 5.66l-9.2 9.19a2 2 0 01-2.83-2.83l8.49-8.48"
    />
  </svg>
)

const ATTACH_ACCEPT = 'image/*,.pdf,.doc,.docx,.xls,.xlsx,.csv'
const MAX_ATTACHMENT_BYTES = 10 * 1024 * 1024

const formatFileSize = (bytes: number): string => {
  if (bytes <= 0) {
    return ''
  }
  if (bytes < 1024) {
    return `${bytes} Б`
  }
  if (bytes < 1024 * 1024) {
    return `${Math.round(bytes / 1024)} КБ`
  }
  return `${(bytes / (1024 * 1024)).toFixed(1)} МБ`
}

type AttachmentBubbleProps = {
  organizationId: number
  counterpartyOrgId: number
  attachment: ChatAttachment
}

const isImageAttachment = (attachment: ChatAttachment): boolean =>
  (attachment.contentType ?? '').startsWith('image/')

const AttachmentBubble = ({ organizationId, counterpartyOrgId, attachment }: AttachmentBubbleProps) => {
  const [objectUrl, setObjectUrl] = useState<string | null>(null)
  const [loadingFile, setLoadingFile] = useState(false)
  const [loadError, setLoadError] = useState(false)

  useEffect(() => {
    if (!isImageAttachment(attachment)) {
      return
    }
    let revoked = false
    let created: string | null = null
    fetchAttachmentObjectUrl(organizationId, counterpartyOrgId, attachment.id)
      .then((url) => {
        if (revoked) {
          URL.revokeObjectURL(url)
          return
        }
        created = url
        setObjectUrl(url)
      })
      .catch(() => setLoadError(true))
    return () => {
      revoked = true
      if (created) {
        URL.revokeObjectURL(created)
      }
    }
  }, [attachment, counterpartyOrgId, organizationId])

  const openFile = async () => {
    if (objectUrl) {
      window.open(objectUrl, '_blank', 'noopener')
      return
    }
    setLoadingFile(true)
    setLoadError(false)
    try {
      const url = await fetchAttachmentObjectUrl(organizationId, counterpartyOrgId, attachment.id)
      window.open(url, '_blank', 'noopener')
    } catch {
      setLoadError(true)
    } finally {
      setLoadingFile(false)
    }
  }

  if (isImageAttachment(attachment) && objectUrl) {
    return (
      <button type="button" className="echat-attachment echat-attachment--image" onClick={() => void openFile()}>
        <img src={objectUrl} alt={attachment.filename} loading="lazy" />
      </button>
    )
  }

  return (
    <button
      type="button"
      className="echat-attachment echat-attachment--file"
      onClick={() => void openFile()}
      disabled={loadingFile}
    >
      <span className="echat-attachment__icon" aria-hidden="true">
        📎
      </span>
      <span className="echat-attachment__info">
        <span className="echat-attachment__name">{attachment.filename}</span>
        <span className="echat-attachment__meta">
          {loadError
            ? 'Не удалось открыть'
            : loadingFile
              ? 'Открываем…'
              : formatFileSize(attachment.sizeBytes)}
        </span>
      </span>
    </button>
  )
}

export const ChatScreen = ({
  organizationId,
  role,
  active,
  partner,
  onSelectPartner,
}: ChatScreenProps) => {
  const [draft, setDraft] = useState('')
  const [pendingFile, setPendingFile] = useState<File | null>(null)
  const [fileError, setFileError] = useState<string | null>(null)
  const [threads, setThreads] = useState<ChatThreadSummary[]>([])
  const [partners, setPartners] = useState<ChatPartner[]>([])
  const [partnersLoading, setPartnersLoading] = useState(false)
  const [partnersError, setPartnersError] = useState<string | null>(null)
  const listRef = useRef<HTMLDivElement | null>(null)
  const messagesEndRef = useRef<HTMLDivElement | null>(null)
  const inputRef = useRef<HTMLTextAreaElement | null>(null)
  const fileInputRef = useRef<HTMLInputElement | null>(null)
  const devPartnerAutoSelectedRef = useRef(false)
  const lastMarkedReadRef = useRef(0)

  const {
    messages,
    loading,
    loadingOlder,
    hasMore,
    error,
    handleSend,
    retryMessage,
    loadOlder,
    isOwnMessage,
  } = useChatPolling(
    organizationId,
    partner?.organizationId ?? null,
    active && partner != null,
  )

  const messageGroups = useMemo(() => groupMessagesByDate(messages), [messages])

  const loadPartners = useCallback(async () => {
    setPartnersLoading(true)
    setPartnersError(null)
    try {
      const [threadItems, partnerItems] = await Promise.all([
        listPartnerThreads(organizationId),
        listChatPartners(organizationId, role),
      ])
      setThreads(threadItems)
      setPartners(partnerItems)
    } catch (loadError) {
      setPartnersError(loadError instanceof Error ? loadError.message : 'Не удалось загрузить чаты')
    } finally {
      setPartnersLoading(false)
    }
  }, [organizationId, role])

  useEffect(() => {
    if (partner != null) {
      return
    }
    void loadPartners()
  }, [loadPartners, partner])

  useEffect(() => {
    if (!isDevMode || partner != null || devPartnerAutoSelectedRef.current) {
      return
    }
    const counterpartyOrgId = devCounterpartyOrgIdForRole(role)
    if (!counterpartyOrgId) {
      return
    }
    devPartnerAutoSelectedRef.current = true
    onSelectPartner({
      organizationId: counterpartyOrgId,
      name: role === 'supplier' ? 'Покупатель (dev)' : 'Поставщик (dev)',
    })
  }, [onSelectPartner, partner, role])

  useEffect(() => {
    if (partner == null) {
      return
    }
    const scrollToBottom = () => {
      messagesEndRef.current?.scrollIntoView({ behavior: 'auto', block: 'end' })
    }
    requestAnimationFrame(scrollToBottom)
  }, [messages, partner?.organizationId, loading])

  useEffect(() => {
    lastMarkedReadRef.current = 0
  }, [partner?.organizationId])

  useEffect(() => {
    if (partner == null || messages.length === 0) {
      return
    }
    const lastId = messages[messages.length - 1].id
    if (lastId <= lastMarkedReadRef.current) {
      return
    }
    lastMarkedReadRef.current = lastId
    markThreadRead(organizationId, partner.organizationId, lastId).catch(() => {
      // Отметка прочитанности — best effort; при сбое её догонит следующий заход.
    })
  }, [messages, organizationId, partner])

  const handleInput = (value: string) => {
    setDraft(value)
    const node = inputRef.current
    if (!node) {
      return
    }
    node.style.height = 'auto'
    node.style.height = `${Math.min(node.scrollHeight, 100)}px`
  }

  const handleFilePick = (file: File | null) => {
    setFileError(null)
    if (!file) {
      setPendingFile(null)
      return
    }
    if (file.size > MAX_ATTACHMENT_BYTES) {
      setPendingFile(null)
      setFileError('Файл больше 10 МБ')
      return
    }
    setPendingFile(file)
  }

  const handleSubmit = () => {
    if (handleSend(draft, pendingFile)) {
      setDraft('')
      setPendingFile(null)
      setFileError(null)
      if (fileInputRef.current) {
        fileInputRef.current.value = ''
      }
      if (inputRef.current) {
        inputRef.current.style.height = ''
      }
    }
  }


  if (!partner) {
    const threadPartnerIds = new Set(threads.map((thread) => thread.counterpartyOrgId))
    const contactsWithoutThread = partners.filter(
      (item) => !threadPartnerIds.has(item.organizationId),
    )

    return (
      <section aria-label="Список чатов">
        {partnersLoading ? <p className="echat-inbox__status">Загрузка…</p> : null}
        {partnersError ? (
          <>
            <p className="echat-error">{partnersError}</p>
            <button type="button" className="echat-btn echat-btn--ghost" onClick={() => void loadPartners()}>
              Повторить
            </button>
          </>
        ) : null}
        {!partnersLoading && threads.length === 0 && partners.length === 0 ? (
          <div className="echat-inbox__empty">
            <p className="echat-inbox__empty-title">Нет чатов</p>
            <p>
              Контакты появятся после первой заявки с {counterpartyLabel(role)} или откройте чат из карточки заявки.
            </p>
          </div>
        ) : null}
        {threads.length > 0 ? (
          <div className="echat-inbox">
            <ul className="echat-inbox__list">
              {threads.map((thread) => (
                <li key={thread.counterpartyOrgId} className="echat-inbox__item">
                  <button
                    type="button"
                    className="echat-inbox__row"
                    onClick={() =>
                      onSelectPartner({
                        organizationId: thread.counterpartyOrgId,
                        name: thread.counterpartyName,
                      })
                    }
                  >
                    <span className="echat-inbox__avatar" aria-hidden="true">
                      {initialsFromName(thread.counterpartyName)}
                    </span>
                    <span className="echat-inbox__body">
                      <span className="echat-inbox__name">{thread.counterpartyName}</span>
                      <span
                        className={
                          thread.unreadCount > 0
                            ? 'echat-inbox__preview echat-inbox__preview--unread'
                            : 'echat-inbox__preview'
                        }
                      >
                        {thread.lastMessageFromMe ? 'Вы: ' : ''}
                        {thread.lastMessagePreview ?? ''}
                      </span>
                    </span>
                    <span className="echat-inbox__meta">
                      <time className="echat-inbox__time">{formatThreadTime(thread.lastMessageAt)}</time>
                      {thread.unreadCount > 0 ? (
                        <span className="echat-inbox__unread" aria-label={`Непрочитанных: ${thread.unreadCount}`}>
                          {thread.unreadCount > 99 ? '99+' : thread.unreadCount}
                        </span>
                      ) : null}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          </div>
        ) : null}
        {contactsWithoutThread.length > 0 ? (
          <>
            <p className="echat-inbox__section-label">Контакты</p>
            <div className="echat-inbox">
              <ul className="echat-inbox__list">
                {contactsWithoutThread.map((item) => (
                  <li key={item.organizationId} className="echat-inbox__item">
                    <button
                      type="button"
                      className="echat-inbox__row"
                      onClick={() =>
                        onSelectPartner({
                          organizationId: item.organizationId,
                          name: item.name,
                        })
                      }
                    >
                      <span className="echat-inbox__avatar" aria-hidden="true">
                        {initialsFromName(item.name)}
                      </span>
                      <span className="echat-inbox__body">
                        <span className="echat-inbox__name">{item.name}</span>
                        <span className="echat-inbox__preview">Начать чат</span>
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          </>
        ) : null}
      </section>
    )
  }

  return (
    <section aria-label={`Чат с ${partner.name}`} className="echat-thread">
      <header className="echat-header">
        <button
          type="button"
          className="echat-header__back"
          aria-label="К списку чатов"
          onClick={() => onSelectPartner(null)}
        >
          ‹
        </button>
        <span className="echat-header__avatar" aria-hidden="true">
          {initialsFromName(partner.name)}
        </span>
        <div className="echat-header__info">
          <h2 className="echat-header__name">{partner.name}</h2>
        </div>
      </header>

      <div ref={listRef} className="echat-messages" aria-live="polite">
        {loading ? <p className="echat-messages__status">Загрузка…</p> : null}
        {!loading && hasMore ? (
          <button
            type="button"
            className="echat-messages__more"
            disabled={loadingOlder}
            onClick={() => void loadOlder()}
          >
            {loadingOlder ? 'Загрузка…' : 'Показать более ранние'}
          </button>
        ) : null}
        {!loading && messages.length === 0 ? (
          <p className="echat-messages__status">Напишите первое сообщение</p>
        ) : null}
        {messageGroups.map((group) => (
          <div key={group.dateKey} className="echat-day">
            <p className="echat-day__label">{group.dateLabel}</p>
            {group.messages.map((message) => {
              const own = isOwnMessage(message)
              const failed = message.status === 'failed'
              const pending = message.status === 'sending'
              return (
                <div
                  key={message.clientId ?? message.id}
                  className={`echat-bubble ${own ? 'echat-bubble--out' : 'echat-bubble--in'}${
                    failed ? ' echat-bubble--failed' : ''
                  }`}
                >
                  {message.attachment ? (
                    <AttachmentBubble
                      organizationId={organizationId}
                      counterpartyOrgId={partner.organizationId}
                      attachment={message.attachment}
                    />
                  ) : message.pendingFileName ? (
                    <span className="echat-attachment echat-attachment--pending">
                      📎 {message.pendingFileName}
                    </span>
                  ) : null}
                  {message.body ? <p className="echat-bubble__text">{message.body}</p> : null}
                  {failed ? (
                    <span className="echat-bubble__status echat-bubble__status--failed">
                      Не отправлено ·{' '}
                      <button
                        type="button"
                        className="echat-bubble__retry"
                        onClick={() => retryMessage(message)}
                      >
                        Повторить
                      </button>
                    </span>
                  ) : (
                    <time className="echat-bubble__time" dateTime={message.createdAt}>
                      {pending ? 'отправка…' : formatMessageTime(message.createdAt)}
                    </time>
                  )}
                </div>
              )
            })}
          </div>
        ))}
        <div ref={messagesEndRef} className="echat-messages__anchor" aria-hidden="true" />
      </div>

      <div className="echat-thread-dock">
        {error ? <p className="echat-error echat-thread-dock__error">{error}</p> : null}
        {fileError ? <p className="echat-error echat-thread-dock__error">{fileError}</p> : null}

        {pendingFile ? (
          <div className="echat-attach-preview">
            <span className="echat-attach-preview__name">
              📎 {pendingFile.name} · {formatFileSize(pendingFile.size)}
            </span>
            <button
              type="button"
              className="echat-attach-preview__remove"
              aria-label="Убрать файл"
              onClick={() => handleFilePick(null)}
            >
              ×
            </button>
          </div>
        ) : null}

        <form
          className="echat-compose"
          onSubmit={(event) => {
            event.preventDefault()
            handleSubmit()
          }}
        >
          <input
            ref={fileInputRef}
            type="file"
            className="visually-hidden"
            accept={ATTACH_ACCEPT}
            onChange={(event) => handleFilePick(event.target.files?.[0] ?? null)}
          />
          <button
            type="button"
            className="echat-compose__attach"
            aria-label="Прикрепить файл"
            onClick={() => fileInputRef.current?.click()}
          >
            <AttachIcon />
          </button>
          <div className="echat-compose__field">
            <label className="visually-hidden" htmlFor="echat-message-input">
              Сообщение
            </label>
            <textarea
              id="echat-message-input"
              ref={inputRef}
              className="echat-compose__input"
              rows={1}
              value={draft}
              placeholder="Сообщение…"
              onChange={(event) => handleInput(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'Enter' && !event.shiftKey) {
                  event.preventDefault()
                  handleSubmit()
                }
              }}
            />
          </div>
          <button
            type="submit"
            className="echat-compose__send"
            disabled={!draft.trim() && !pendingFile}
            aria-label="Отправить"
          >
            <SendIcon />
          </button>
        </form>
      </div>
    </section>
  )
}
